import { CommonModule } from '@angular/common';
import { scrollToFirstError } from '@anihis/shared/src/functions/scrollToFirstError';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import {
  FormsModule,
  ReactiveFormsModule,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { FormBaseComponent } from '@anihis/shared/src/shared/base-components/form-base.component';
import { TranslocoModule } from '@jsverse/transloco';
import { RegexPatterns } from '@anihis/shared/src/constants/regex-patterns';
import { MatError } from '@angular/material/form-field';
import { ResetPasswordStore } from './reset-password.store';
import { LoadingStatus } from '../../shared/enums/loading';

@Component({
  selector: 'anihis-reset-password',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslocoModule,
    MatButtonModule,
    MatError,
    RouterLink,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ResetPasswordStore],
})
export class ResetPasswordComponent extends FormBaseComponent {
  store = inject(ResetPasswordStore);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  readonly status = LoadingStatus;
  showValidationErrors = signal(false);
  showPassword = signal(false);
  showPassword2 = signal(false);

  email = signal<string>('');
  userId = signal<string>('');
  token = signal<string>('');
  role = signal<string>('');

  passwordChecks = signal({
    length: false,
    lower: false,
    upper: false,
    number: false,
    special: false,
  });

  constructor() {
    super();
    this.initializeForm();

    this.form.get('passwordNew')!.valueChanges.subscribe((v: string | null) => {
      const val = v ?? '';
      this.passwordChecks.set({
        length: val.length >= 8,
        lower: /[a-z]/.test(val),
        upper: /[A-Z]/.test(val),
        number: /[0-9]/.test(val),
        special: /[^a-zA-Z0-9]/.test(val),
      });
    });

    this.route.queryParams.subscribe((params) => {
      this.email.set(params['email'] || '');
      this.userId.set(params['userId'] || '');
      this.token.set(params['token'] || '');
      this.role.set((params['role'] || '').toLowerCase());
    });
  }

  private passwordsMatchValidator: ValidatorFn = (control: AbstractControl) => {
    const pass = control.get('passwordNew')?.value;
    const confirm = control.get('passwordNew2')?.value;

    if (!pass || !confirm) {
      return null; // ne diže grešku dok oba nisu popunjena
    }

    return pass === confirm ? null : { passwordsDontMatch: true };
  };

  private initializeForm() {
    this.form = this.fb.group(
      {
        passwordNew: [
          '',
          [
            Validators.required,
            Validators.pattern(RegexPatterns.PASSWORD_STRENGTH_REGEX),
          ],
        ],
        passwordNew2: ['', [Validators.required]],
      },
      {
        validators: this.passwordsMatchValidator,
      },
    );
  }

  resetPassword() {
    this.showValidationErrors.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      setTimeout(() => scrollToFirstError(), 50);
      return;
    }

    const newPassword = this.form.get('passwordNew')?.value;
    this.store
      .resetPassword(newPassword, this.userId(), this.token())
      .subscribe((ok: boolean) => {
        this.cdr.markForCheck();
        if (ok) {
          this.form.reset();
          this.showValidationErrors.set(false);
        }
      });
  }
}
